import * as React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  Alert,
  Skeleton,
} from "@mui/material";
import { useQuery, useMutation } from "@apollo/client";
import {
  ANNOUNCEMENT_BANNER_QUERY,
  ADMIN_UPSERT_ANNOUNCEMENT_BANNER,
} from "../../graphql/announcementBanner";
import AnnouncementBannerFormDialog from "../Components/AnnouncementBannerFormDialog";

export default function AnnouncementBannerAdminPage() {
  const { data, loading, error, refetch } = useQuery(
    ANNOUNCEMENT_BANNER_QUERY,
    {
      fetchPolicy: "cache-and-network",
    },
  );

  const [open, setOpen] = React.useState(false);
  const [serverError, setServerError] = React.useState<string | null>(null);

  const banner = data?.announcementBanner;

  const [upsertBanner, { loading: saving }] = useMutation(
    ADMIN_UPSERT_ANNOUNCEMENT_BANNER,
    {
      onCompleted: (res) => {
        const errs = res?.adminUpsertAnnouncementBanner?.errors || [];
        if (errs.length) {
          setServerError(errs.join(", "));
          return;
        }
        setServerError(null);
        setOpen(false);
      },
      onError: (e) => setServerError(e.message),
      refetchQueries: [{ query: ANNOUNCEMENT_BANNER_QUERY }],
    },
  );

  const handleSave = async (values: {
    message: string;
    backgroundColor: string;
    textColor: string;
  }) => {
    setServerError(null);
    await upsertBanner({
      variables: {
        message: values.message,
        backgroundColor: values.backgroundColor,
        textColor: values.textColor,
      },
    });
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800 }}>
            Announcement Banner
          </Typography>
          <Typography sx={{ color: "text.secondary", mt: 0.5 }}>
            Control the black top bar message shown on the website header.
          </Typography>
        </Box>

        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={() => refetch()}>
            Refresh
          </Button>
          <Button variant="contained" onClick={() => setOpen(true)}>
            Edit Banner
          </Button>
        </Stack>
      </Stack>

      {error ? <Alert severity="error">{error.message}</Alert> : null}

      <Card sx={{ borderRadius: 3 }}>
        <CardContent>
          {loading ? (
            <Stack spacing={2}>
              <Skeleton height={28} width={220} />
              <Skeleton height={20} width="70%" />
              <Skeleton variant="rectangular" height={44} />
            </Stack>
          ) : (
            <Stack spacing={2}>
              <Stack direction="row" justifyContent="space-between">
                <Typography sx={{ fontWeight: 700 }}>Current Banner</Typography>
              </Stack>

              <Typography sx={{ color: "text.secondary" }}>Message:</Typography>
              <Typography sx={{ fontWeight: 700 }}>
                {banner?.message || "—"}
              </Typography>

              <Typography sx={{ color: "text.secondary" }}>Preview:</Typography>
              <Box
                sx={{
                  p: "10px 12px",
                  textAlign: "center",
                  fontSize: 13,
                  letterSpacing: 0.2,
                  borderRadius: 1.5,
                  bgcolor: banner?.backgroundColor || "#000",
                  color: banner?.textColor || "#fff",
                  opacity: 1,
                }}
              >
                {banner?.message ||
                  "30 Days Free Returns | Free Shipping Lahore wide | Lifetime Warranty"}
              </Box>
            </Stack>
          )}
        </CardContent>
      </Card>

      <AnnouncementBannerFormDialog
        open={open}
        onClose={() => {
          setOpen(false);
          setServerError(null);
        }}
        initial={{
          message: banner?.message,
          backgroundColor: banner?.backgroundColor,
          textColor: banner?.textColor,
        }}
        onSave={handleSave}
        saving={saving}
        serverError={serverError}
      />
    </Box>
  );
}
